import React, { useEffect, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from 'components/Popover';
import { Input } from 'components/input/Input';
import { Maybe } from 'types/UtilityTypes';

export type AutoCompleteInputOptionType = {
  label: React.ReactNode;
  value: string;
};

export interface AutoCompleteInputProps {
  options?: AutoCompleteInputOptionType[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string, option: AutoCompleteInputOptionType) => void;
  onSearch?: (searchText: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactElement;
  suffixIcon?: Maybe<React.ReactNode>;
  filterOption?:
    boolean | ((input: string, option: AutoCompleteInputOptionType) => boolean);
}

export const AutoCompleteInput: React.FC<AutoCompleteInputProps> = ({
  options = [],
  value,
  defaultValue,
  onChange,
  onSelect,
  onSearch,
  placeholder,
  disabled = false,
  className,
  children,
  suffixIcon: _suffixIcon = null,
  filterOption = true,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(value || defaultValue || '');

  useEffect(() => {
    if (value !== undefined) {
      setSearchValue(value);
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setSearchValue(newValue);
    onChange?.(newValue);
    onSearch?.(newValue);
    setOpen(true);
  };

  const handleSelect = (optionValue: string) => {
    const option = options.find((opt) => opt.value === optionValue);

    setSearchValue(optionValue);
    setOpen(false);
    onChange?.(optionValue);

    if (option) {
      onSelect?.(optionValue, option);
    }
  };

  const filteredOptions = React.useMemo(() => {
    if (!filterOption || !searchValue) return options;

    if (typeof filterOption === 'function') {
      return options.filter((option) => filterOption(searchValue, option));
    }

    return options.filter((option) =>
      String(option.label || option.value)
        .toLowerCase()
        .includes(searchValue.toLowerCase())
    );
  }, [options, searchValue, filterOption]);

  const inputElement = children || (
    <Input
      value={searchValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );

  return (
    <Popover open={open && filteredOptions.length > 0} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{inputElement}</PopoverTrigger>
      <PopoverContent
        className='w-[var(--radix-popover-trigger-width)] p-1'
        align='start'
      >
        <div className='max-h-[300px] overflow-y-auto'>
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className='px-2 py-1.5 text-sm cursor-pointer rounded-sm transition-colors hover:bg-neutral-100'
            >
              {option.label}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AutoCompleteInput;
